import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore - package ships its own types inconsistently across versions
import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserPool,
} from 'amazon-cognito-identity-js';

import { API_BASE_URL, COGNITO_CLIENT_ID } from '../../config';
import { AuthShell } from '../../components/AuthShell';

type UserMe = {
  tenantId: string;
  orgName: string;
  userId: string;
  name: string;
  role: string;
};

async function fetchUserMe(idToken: string): Promise<UserMe | null> {
  const resp = await fetch(`${API_BASE_URL}/users/me`, {
    headers: {
      Authorization: `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    },
  });

  if (resp.status === 404) return null;
  if (!resp.ok) throw new Error('Failed to fetch user profile');
  return (await resp.json()) as UserMe;
}

export const SetPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const tempEmail = sessionStorage.getItem('tempEmail') || '';
  const tempPassword = sessionStorage.getItem('tempPassword') || '';

  const userPool = useMemo(() => {
    return new CognitoUserPool({
      UserPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID,
      ClientId: COGNITO_CLIENT_ID,
    });
  }, []);

  useEffect(() => {
    if (!tempEmail || !tempPassword) {
      navigate('/login');
    }
  }, [navigate, tempEmail, tempPassword]);

  const handleSetPassword = async () => {
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsSubmitting(true);

    try {
      const authenticationDetails = new AuthenticationDetails({
        Username: tempEmail,
        Password: tempPassword,
      });

      const cognitoUser = new CognitoUser({
        Username: tempEmail,
        Pool: userPool,
      });

      await new Promise<void>((resolve, reject) => {
        cognitoUser.authenticateUser(authenticationDetails, {
          onSuccess: () => {
            reject(new Error('Unexpected success during password challenge'));
          },
          onFailure: (err: any) => reject(err),
          newPasswordRequired: (userAttributes: any) => {
            // Remove attributes that are already set or verified
            delete userAttributes.email_verified;
            delete userAttributes.phone_number_verified;
            delete userAttributes.email;
            delete userAttributes.sub;

            cognitoUser.completeNewPasswordChallenge(newPassword, userAttributes, {
              onSuccess: async (result: any) => {
                try {
                  const idToken = result.getIdToken().getJwtToken();
                  const accessToken = result.getAccessToken().getJwtToken();

                  localStorage.setItem('idToken', idToken);
                  localStorage.setItem('accessToken', accessToken);
                  localStorage.setItem('userEmail', tempEmail);

                  sessionStorage.removeItem('tempEmail');
                  sessionStorage.removeItem('tempPassword');

                  const me = await fetchUserMe(idToken);
                  if (!me) {
                    navigate('/no-access');
                    resolve();
                    return;
                  }

                  localStorage.setItem('tenantId', me.tenantId);
                  localStorage.setItem('orgName', me.orgName);
                  localStorage.setItem('userId', me.userId);
                  localStorage.setItem('userName', me.name);
                  localStorage.setItem('userRole', me.role);

                  navigate('/dashboard');
                  resolve();
                } catch (e) {
                  reject(e);
                }
              },
              onFailure: (err: any) => reject(err),
            });
          },
        });
      });
    } catch (e: any) {
      setError(e?.message || 'Failed to set password');
      setIsSubmitting(false);
      return;
    }

    setIsSubmitting(false);
  };

  return (
    <AuthShell>
      <div className="auth-heading">
        <h3>Set new password</h3>
        <p>Please choose a new password for your account</p>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          void handleSetPassword();
        }}
      >
        <div className="np-field">
          <label>New Password</label>
          <input
            type="password"
            className="np-input"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
            minLength={8}
            placeholder="At least 8 characters"
          />
          <p className="np-hint">Must contain uppercase, lowercase, number, and special character</p>
        </div>
        <div className="np-field">
          <label>Confirm Password</label>
          <input
            type="password"
            className="np-input"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
          />
        </div>

        {error ? (
          <div className="np-banner np-tone-danger mb-3"><i className="ri-error-warning-line"></i>{error}</div>
        ) : null}

        <button type="submit" className="np-btn np-btn-primary w-100 justify-content-center" disabled={isSubmitting}>
          {isSubmitting ? (
            <>
              <span className="spinner-border spinner-border-sm" />
              Setting password…
            </>
          ) : (
            <>
              <i className="ri-lock-line"></i>
              Set Password
            </>
          )}
        </button>
      </form>
    </AuthShell>
  );
};
